import { useEffect, useRef, useState } from "react";
import { ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MB = 1024 * 1024;

export const BrandLogoCard = () => {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/settings").then((r) => setLogo(r.data?.brand_logo_url || "")).catch(() => setLogo("")); }, []);

  const save = async (url) => {
    try {
      await api.put("/settings", { brand_logo_url: url });
      setLogo(url);
      toast.success(url ? "Logo insegna salvato" : "Logo insegna rimosso");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Salvataggio non riuscito"); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * MB) { toast.error("Logo troppo grande: massimo 2 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/media", fd);
      await save(data.url);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Caricamento logo non riuscito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (logo === null) return null;

  return (
    <div className="bg-[#111827]/80 backdrop-blur-md border border-[#1F2937] rounded-2xl p-6" data-testid="brand-logo-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
          <ImageIcon className="h-6 w-6 text-[#F59E0B]" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-200">Logo insegna</h3>
          <p className="text-xs text-slate-500">Sovrapposto alle immagini dei template nel ritaglio guidato · PNG/JPG, max 2 MB</p>
        </div>
      </div>

      <div className="flex items-center gap-5 mt-5 flex-wrap">
        <div className="h-24 w-24 rounded-xl border border-dashed border-[#1F2937] bg-[#0A0E17] flex items-center justify-center overflow-hidden shrink-0"
             data-testid="brand-logo-preview">
          {logo ? <img src={logo} alt="Logo insegna" className="max-h-full max-w-full object-contain p-2" />
                : <span className="text-[11px] text-slate-600 text-center px-2">Nessun logo</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                 data-testid="brand-logo-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="brand-logo-upload-button"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {logo ? "Sostituisci logo" : "Carica logo"}
          </Button>
          {logo && (
            <Button variant="outline" onClick={() => save("")} disabled={busy} data-testid="brand-logo-remove-button"
                    className="border-[#1F2937] text-slate-300 hover:bg-[#1F2937] hover:text-slate-100">
              <Trash2 className="h-4 w-4 mr-2" /> Rimuovi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
